package com.yogaaicoach.pages.auth

import com.yogaaicoach.pages.auth.EmailPasswordSignInViewModel
import com.yogaaicoach.user.AppViewModel
import com.yogaaicoach.scope
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.span
import react.useEffectOnce
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.email
import web.html.password
import web.html.submit as submitType

internal external interface EmailSignInPageProps : Props {
    var viewModel: EmailPasswordSignInViewModel
    var appViewModel: AppViewModel
    var onNavigateToCreateAccount: () -> Unit
    var onNavigateToForgotPassword: () -> Unit
    var onNavigateToVerifyEmail: (String) -> Unit
}

// Pixel-matched against EmailPasswordSignInScreen.kt (AuthPageLayout + FormSectionCard +
// AuthComponents.kt's EmailRow/PasswordRow/ErrorRow). Navigating here changes the URL hash
// (see AppRoot.kt), so the browser's native back button returns to the login page.
internal val EmailSignInPage = FC<EmailSignInPageProps> { props ->
    val viewModel = props.viewModel

    var email by useState(viewModel.email.value)
    var password by useState(viewModel.password.value)
    var error by useState(viewModel.error.value)
    var isLoading by useState(viewModel.isLoading.value)
    var showVerificationResendLink by useState(viewModel.showVerificationResendLink.value)

    useEffectOnce {
        scope.launch { viewModel.email.collect { email = it } }
        scope.launch { viewModel.password.collect { password = it } }
        scope.launch { viewModel.error.collect { error = it } }
        scope.launch { viewModel.isLoading.collect { isLoading = it } }
        scope.launch { viewModel.showVerificationResendLink.collect { showVerificationResendLink = it } }
    }

    div {
        className = ClassName("auth-page")

        img { className = ClassName("auth-logo"); src = "logo.png"; alt = "App logo" }
        p { className = ClassName("auth-brand"); +"Yoga AI Coach" }

        div {
            className = ClassName("form-section-card")

            p { className = ClassName("form-title"); +"Welcome Back" }

            // Always rendered (not just when an error is present) so the card doesn't jump —
            // matches ErrorRow's reserved-Spacer-when-empty behavior.
            p { className = ClassName("auth-error"); +error }

            if (showVerificationResendLink) {
                button {
                    type = ButtonType.buttonType
                    className = ClassName("text-button")
                    +"Resend Verification Email"
                    onClick = { viewModel.onResendVerificationEmail { email -> props.onNavigateToVerifyEmail(email) } }
                }
            }

            // A real <form> with onSubmit, not just an onClick on the Sign In button, so
            // pressing Enter in either field submits — standard browser/UI behavior. The other
            // buttons inside need an explicit type="button" so they don't also trigger submit.
            form {
                className = ClassName("auth-form")
                onSubmit = { event ->
                    event.preventDefault()
                    viewModel.onSignIn(props.appViewModel::handleUserLoginSuccess)
                }

                FloatingLabelInput("email-input", "Email", InputType.email, email) { viewModel.onEmailChange(it) }
                FloatingLabelInput("password-input", "Password", InputType.password, password) { viewModel.onPasswordChange(it) }

                div {
                    className = ClassName("forgot-password-row")
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("text-button")
                        +"Forgot password?"
                        onClick = { props.onNavigateToForgotPassword() }
                    }
                }

                button {
                    type = ButtonType.submitType
                    className = ClassName(if (isLoading) "primary-button loading" else "primary-button")
                    disabled = isLoading
                    if (isLoading) {
                        span { className = ClassName("button-spinner") }
                    } else {
                        +"Sign In"
                    }
                }
            }
        }

        div {
            className = ClassName("create-account-row")
            p { +"Don't have an account?" }
            button {
                className = ClassName("text-button")
                +"Create account"
                onClick = { props.onNavigateToCreateAccount() }
            }
        }
    }
}
